import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight, Sparkles, Code2, Palette, TrendingUp, Smartphone, ShoppingBag } from 'lucide-react';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { fetchServices, STATIC } from '@/lib/cms';
import SEO from '@/components/site/SEO';

const ICONS = { Sparkles, Code2, Palette, TrendingUp, Smartphone, ShoppingBag };

export default function Services() {
  const [items, setItems] = useState(STATIC.services);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  useEffect(() => { fetchServices().then(setItems); }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const filtered = items.filter(i => (cat === 'All' || i.category === cat) && (q === '' || i.title.toLowerCase().includes(q.toLowerCase()) || i.short?.toLowerCase().includes(q.toLowerCase())));

  return (
    <>
      <SEO title="Services — Slinkflow" description="Explore Slinkflow's services — web design, development, brand identity, SEO, mobile apps, and ecommerce." />
      <section className="relative overflow-hidden pt-40 pb-16 lg:pt-56 lg:pb-24">
        <div className="absolute -top-32 left-0 h-[520px] w-[520px] hero-gradient-orb" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Services</p>
          <h1 className="mt-6 max-w-5xl font-display text-[56px] font-light leading-[1.2] tracking-[-0.03em] md:text-[88px] lg:text-[104px]">
            <TextReveal text="Everything we ship," />
            <br />
            <span className="gradient-text font-medium"><TextReveal text="in one place." delay={0.2} /></span>
          </h1>
          <FadeUp delay={0.4} className="mt-10 flex flex-wrap items-center gap-4">
            <div className="flex w-full max-w-md items-center gap-2 rounded-full border border-ink-100 bg-white px-5 py-3">
              <Search className="h-4 w-4 text-ink-400" />
              <input data-testid="services-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services" className="flex-1 bg-transparent text-[15px] text-ink-900 placeholder:text-ink-400 focus:outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <button key={c} onClick={() => setCat(c)} data-testid={`services-filter-${c.toLowerCase()}`} className={`rounded-full px-4 py-2 text-sm transition ${cat === c ? 'bg-ink-900 text-white' : 'border border-ink-100 bg-white text-ink-700 hover:border-cyan hover:text-cyan'}`}>{c}</button>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="pb-32 lg:pb-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s, i) => {
              const Icon = ICONS[s.icon] || Sparkles;
              return (
                <FadeUp key={s.id} delay={i * 0.04}>
                  <Link to={`/services/${s.slug}`} data-cursor data-cursor-label="View" data-testid={`services-card-${s.slug}`} className="group flex h-full flex-col rounded-3xl border border-ink-100 bg-white p-8 transition-all hover:-translate-y-1 hover:border-cyan/40 hover:shadow-hover">
                    <div className="mb-8 flex items-center justify-between">
                      <span className="font-display text-xs text-ink-300">0{s.order_index}</span>
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-ink-50 text-ink-900 transition group-hover:bg-cyan group-hover:text-white">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                    </div>
                    <h3 className="font-display text-[26px] text-ink-900">{s.title}</h3>
                    <p className="mt-1 text-[14px] text-cyan">{s.tagline}</p>
                    <p className="mt-5 text-[15px] leading-relaxed text-ink-500">{s.short}</p>
                    <div className="mt-auto pt-8 flex items-center gap-2 text-sm font-medium text-ink-900">
                      Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                    </div>
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
